import React from "react";
import { Link } from "react-router-dom";
import { S } from "./ibloom/styles";

/**
 * Static privacy policy. Required by Google Play Console (the listing form has
 * a mandatory "Privacy policy URL" field) and by Indian DPDP. Plain HTML, no
 * dependencies — keeps the page tiny and crawlable so the Play Store reviewer
 * can fetch it without needing JS.
 */
export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" style={{ maxWidth: 760, margin: "0 auto", padding: "32px 20px 56px", color: "#1A1A1A", lineHeight: 1.65 }}>
      <Link to="/app" style={{ color: "#0F3D2E", fontSize: 13, fontWeight: 600, textDecoration: "none" }}>← Back to Mapbazar</Link>
      <h1 style={{ ...S.fraunces, fontSize: 36, fontWeight: 700, marginTop: 14, marginBottom: 6 }}>Privacy Policy</h1>
      <p style={{ color: "#666", fontSize: 13, marginBottom: 26 }}>Last updated: 16 February 2026 · Operator: Mapbazar (Ahmedabad, India)</p>

      <Section title="1. Who we are">
        Mapbazar (“we”, “our”, “us”) operates the website <b>mapbazar.com</b> and the Mapbazar
        Android app. We are based in Ahmedabad, India. For privacy questions write to <b>kirit@mapbazar.com</b>.
      </Section>

      <Section title="2. What we collect">
        <ul style={ulStyle}>
          <li><b>Account data</b> — name, email or mobile number, and (for shopkeepers) shop name + WhatsApp number you enter at sign-up.</li>
          <li><b>Approximate location</b> — your latitude/longitude or pincode, used only to show shops near you. Stored locally on your device; the server only sees it to filter listings.</li>
          <li><b>Order data</b> — items ordered, delivery address you enter, order status, and rewards earned.</li>
          <li><b>Product uploads</b> — photos and descriptions you submit through ShopKeeper AI (for shopkeepers only).</li>
          <li><b>Usage data</b> — pages visited, search queries, anonymous device/browser info. Used to improve search and prevent abuse.</li>
        </ul>
        We do <b>not</b> collect government IDs, biometric data, health records, payment card numbers (handled by the payment gateway), or contact lists.
      </Section>

      <Section title="3. Why we collect it">
        <ul style={ulStyle}>
          <li>Show you nearby shops and stock on the map.</li>
          <li>Send you order updates and reward notifications.</li>
          <li>Generate AI listings for shopkeepers (ShopKeeper AI).</li>
          <li>Keep the service secure (rate limiting, fraud detection).</li>
          <li>Comply with applicable law in India (DPDP Act 2023, IT Act).</li>
        </ul>
      </Section>

      <Section title="4. Who we share data with">
        Only the minimum needed to deliver the service:
        <ul style={ulStyle}>
          <li><b>Shopkeepers</b> see your name and delivery address only after you place an order with them.</li>
          <li><b>Payment processors</b> (e.g. Stripe / Razorpay) process card details directly — Mapbazar never stores them.</li>
          <li><b>Cloud hosting</b> (Emergent / AWS) stores the database and uploaded images. They cannot read content without our consent.</li>
          <li><b>AI providers</b> (Google Gemini, Sarvam AI) receive only the text/image you submit for AI generation. Inputs are not used to train their models per the API terms in effect.</li>
        </ul>
        We <b>never sell</b> your personal data.
      </Section>

      <Section title="5. How long we keep it">
        <ul style={ulStyle}>
          <li>Account data — until you ask us to delete it.</li>
          <li>Orders — 7 years (tax / consumer-protection requirement under Indian law).</li>
          <li>Anonymous usage logs — 90 days.</li>
        </ul>
      </Section>

      <Section title="6. Your rights">
        You can at any time:
        <ul style={ulStyle}>
          <li>Ask for a copy of the data we hold about you.</li>
          <li>Correct anything wrong.</li>
          <li>Delete your account — email <b>kirit@mapbazar.com</b> with the subject “Delete my account” from the email/mobile linked to your account.</li>
          <li>Withdraw consent for marketing messages.</li>
        </ul>
        We respond within 30 days, faster for urgent requests.
      </Section>

      <Section title="7. Children">
        Mapbazar is not intended for children under 13. We do not knowingly collect data from children. If you believe we have, write to us and we will delete it.
      </Section>

      <Section title="8. Security">
        Data in transit is encrypted with HTTPS. Passwords are stored hashed with bcrypt. We use role-based access internally and review access logs regularly. No system is perfectly secure, but we treat your data the way we treat our own.
      </Section>

      <Section title="9. Changes to this policy">
        We will update this page if our practices change and bump the “Last updated” date at the top. Major changes will also be flagged inside the app.
      </Section>

      <Section title="10. Grievance officer">
        Per Rule 5(9) of the IT Rules 2011, the grievance officer is:<br />
        <b>Kirit Patel</b>, Mapbazar, Ahmedabad, Gujarat, India · <b>kirit@mapbazar.com</b>
      </Section>

      <div style={{ marginTop: 32, padding: 16, background: "#FAF9F6", border: "1px solid #EAE8DC", borderRadius: 12, fontSize: 12.5, color: "#555" }}>
        This policy is plain-language by design. If you want the formal legal version or need it in Hindi/Gujarati, write to us and we will send it.
      </div>
    </div>
  );
}

const ulStyle = { paddingLeft: 22, margin: "10px 0 18px" };

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 22 }}>
      <h2 style={{ ...S.fraunces, fontSize: 19, fontWeight: 700, marginBottom: 8, color: "#0F3D2E" }}>{title}</h2>
      <div style={{ fontSize: 14.5, color: "#1A1A1A" }}>{children}</div>
    </section>
  );
}
